import React, { useState } from "react";
import { Chip } from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";

import BaseQuestionCard from "../BaseQuestionCard";
import useChoiceOptions from "../hook/useChoiceOptions";
import {
  QUESTION_CARD_VARIANTS,
  SURVEY_TYPES,
  SINGLE_CHOICE_CARD_MENU_LABELS,
  SINGLE_CHOICE_CARD_TEXTS,
  SINGLE_CHOICE_CARD_STYLES,
  SINGLE_CHOICE_CARD_DEFAULTS,
  CONDITIONAL_MENU_ITEMS,
  NO_CONDITIONAL_TYPE
} from "../constants/constantsSurveys";
import {
  canAddConditionalToQuestion,
  questionHasNestedChildren,
  normalizeChildrenDisplayIndexes,
  shouldHighlightSingleChoiceCard
} from "../utils/choiceQuestionUtils";

import SingleChoiceOptionsSection from "./singleChoice/SingleChoiceOptionsSection";
import SingleChoiceConditionalMenu from "./singleChoice/SingleChoiceConditionalMenu";

export default function SingleChoiceQuestionCard(props) {
  const {
    question,
    onUpdate,
    index,
    showValidation,
    isNested = false,
    hideDuplicate = false,
    forceCanAddConditional = null,
    externalEnableConditional = null,
    parentOptionLabel = "",
    onDelete,
    onDuplicate,
    dragHandleProps = {},
    cardSxOverride: externalCardSxOverride = null,
    headerSxOverride: externalHeaderSxOverride = null,
    questionTextSxOverride: externalQuestionTextSxOverride = null,
    chipSxOverride: externalChipSxOverride = null,
    questionLabel: externalQuestionLabel = null
  } = props;

  const [conditionalMenu, setConditionalMenu] = useState({
    anchorEl: null,
    optionId: null
  });

  const currentLevel = question.level || SINGLE_CHOICE_CARD_DEFAULTS.initialLevel;
  const displayIndex = question.displayIndex || `${index + 1}`;
  const hasChildQuestions = questionHasNestedChildren(question);
  const isConditionalParent = Boolean(question.isConditionalParent || hasChildQuestions);

  const {
    options,
    inputRefs,
    handleOptionChange,
    handleAddOption,
    handleKeyDown,
    getInputValue
  } = useChoiceOptions(question, onUpdate);

  const canAddConditional =
    forceCanAddConditional !== null
      ? forceCanAddConditional
      : canAddConditionalToQuestion(question);

  const shouldUseOrangeStyle = shouldHighlightSingleChoiceCard(
    question,
    isNested
  );

  const parentOptionTextColor = SINGLE_CHOICE_CARD_STYLES.defaultParentOptionTextColor;

  const resolvedCardSxOverride = {
    ...(shouldUseOrangeStyle
      ? SINGLE_CHOICE_CARD_STYLES.highlightCardSx
      : {}),
    ...(externalCardSxOverride || {})
  };

  const resolvedHeaderSxOverride = {
    ...(shouldUseOrangeStyle
      ? SINGLE_CHOICE_CARD_STYLES.highlightHeaderSx
      : {}),
    ...(externalHeaderSxOverride || {})
  };

  const resolvedQuestionTextSxOverride = {
    ...(externalQuestionTextSxOverride || {})
  };

  const handleEnableConditional = () => {
    if (externalEnableConditional) {
      externalEnableConditional();
      return;
    }

    onUpdate?.({ isConditionalParent: true });
  };

  const handleDisableConditional = () => {
    if (hasChildQuestions) {
      const confirmed = window.confirm(
        SINGLE_CHOICE_CARD_TEXTS.removeConditionalConfirmation
      );

      if (!confirmed) return;
    }

    const cleanedOptions = (options || []).map((option) => ({
      ...option,
      childQuestions: []
    }));

    onUpdate?.({
      isConditionalParent: false,
      options: normalizeChildrenDisplayIndexes(cleanedOptions, displayIndex)
    });
  };

  const requestDeleteOption = (optionIndex) => {
    if (options.length <= 1) return;

    const option = options[optionIndex];
    const hasChildren = (option?.childQuestions || []).length > 0;

    if (hasChildren) {
      const confirmed = window.confirm(
        SINGLE_CHOICE_CARD_TEXTS.deleteOptionConfirmation
      );

      if (!confirmed) return;
    }

    const newOptions = options.filter((_, idx) => idx !== optionIndex);

    onUpdate?.({
      options: normalizeChildrenDisplayIndexes(newOptions, displayIndex)
    });
  };

  const requestDeleteQuestion = () => {
    if (hasChildQuestions) {
      const confirmed = window.confirm(
        SINGLE_CHOICE_CARD_TEXTS.deleteQuestionConfirmation
      );

      if (!confirmed) return;
    }

    onDelete?.();
  };

  const handleOpenConditionalMenu = (event, optionId, isDisabled) => {
    if (isDisabled) return;

    setConditionalMenu({
      anchorEl: event.currentTarget,
      optionId
    });
  };

  const handleCloseConditionalMenu = () => {
    setConditionalMenu({
      anchorEl: null,
      optionId: null
    });
  };

  const handleSelectConditionalType = (type) => {
    const optionId = conditionalMenu.optionId;
    if (!optionId) return;

    const selectedOption = options.find((opt) => opt.id === optionId);
    if (!selectedOption) return;

    const existingChildren = selectedOption.childQuestions || [];
    const firstChild = existingChildren[0];
    const hasExistingChild = existingChildren.length > 0;

    if (type === NO_CONDITIONAL_TYPE) {
      if (!hasExistingChild) {
        handleCloseConditionalMenu();
        return;
      }

      const clearedOptions = options.map((option) =>
        option.id === optionId
          ? {
              ...option,
              childQuestions: []
            }
          : option
      );

      onUpdate?.({
        options: normalizeChildrenDisplayIndexes(clearedOptions, displayIndex)
      });
      handleCloseConditionalMenu();
      return;
    }

    if (hasExistingChild) {
      const sameType = firstChild?.type === type;

      if (sameType) {
        handleCloseConditionalMenu();
        return;
      }

      const confirmed = window.confirm(
        SINGLE_CHOICE_CARD_TEXTS.replaceConditionalConfirmation
      );

      if (!confirmed) {
        handleCloseConditionalMenu();
        return;
      }
    }

    const optionIndex = options.findIndex((opt) => opt.id === optionId);
    const optionNumber =
      optionIndex >= 0
        ? optionIndex + 1
        : SINGLE_CHOICE_CARD_DEFAULTS.firstChildIndex;
    const optionDisplayIndex = `${displayIndex}.${optionNumber}`;

    const newOptions = options.map((option) => {
      if (option.id !== optionId) {
        return option;
      }

      if (hasExistingChild) {
        return {
          ...option,
          childQuestions: [
            buildConditionalChildQuestion({
              type,
              level: currentLevel + 1,
              displayIndex: optionDisplayIndex,
              existingId: firstChild?.id
            })
          ]
        };
      }

      return {
        ...option,
        childQuestions: [
          ...existingChildren,
          buildConditionalChildQuestion({
            type,
            level: currentLevel + 1,
            displayIndex: optionDisplayIndex
          })
        ]
      };
    });

    onUpdate?.({
      options: normalizeChildrenDisplayIndexes(newOptions, displayIndex)
    });

    handleCloseConditionalMenu();
  };

  const buildDefaultConditionalOptions = () => [
    {
      id: crypto.randomUUID(),
      label: "",
      childQuestions: []
    },
    {
      id: crypto.randomUUID(),
      label: "",
      childQuestions: []
    }
  ];

  const buildConditionalChildQuestion = ({
    type,
    level,
    displayIndex: childDisplayIndex,
    existingId = null
  }) => ({
    id: existingId || crypto.randomUUID(),
    uid: crypto.randomUUID(),
    type,
    question: "",
    response: "",
    descriptions: "",
    level,
    displayIndex: childDisplayIndex,
    image_requirement: "NO_IMAGE",
    isConditionalParent: false,
    shouldValidate: false,
    options:
      type === SURVEY_TYPES.singleChoice || type === SURVEY_TYPES.multipleChoice
        ? buildDefaultConditionalOptions()
        : []
  });

  const menuItems = [
    ...(!hideDuplicate
      ? [
        {
          label: SINGLE_CHOICE_CARD_MENU_LABELS.duplicate,
          icon: <ContentCopyIcon sx={{ mr: 1 }} fontSize="small" />,
          onClick: onDuplicate
        }
      ]
      : []),
    ...(canAddConditional
      ? [
        isConditionalParent
          ? {
            label: SINGLE_CHOICE_CARD_MENU_LABELS.removeConditional,
            icon: (
              <AccountTreeOutlinedIcon sx={{ mr: 1 }} fontSize="small" />
            ),
            onClick: handleDisableConditional
          }
          : {
            label: SINGLE_CHOICE_CARD_MENU_LABELS.addConditional,
            icon: (
              <AccountTreeOutlinedIcon sx={{ mr: 1 }} fontSize="small" />
            ),
            onClick: handleEnableConditional
          }
      ]
      : []),
    {
      label: SINGLE_CHOICE_CARD_MENU_LABELS.delete,
      icon: <DeleteOutlineIcon sx={{ mr: 1 }} fontSize="small" />,
      onClick: requestDeleteQuestion
    }
  ];

  const selectedConditionalOption = options.find(
    (option) => option.id === conditionalMenu.optionId
  );
  const selectedConditionalValue =
    selectedConditionalOption?.childQuestions?.[0]?.type || NO_CONDITIONAL_TYPE;

  return (
    <>
      <BaseQuestionCard
        question={question}
        index={index}
        showValidation={showValidation}
        dragHandleProps={dragHandleProps}
        onDelete={requestDeleteQuestion}
        onDuplicate={onDuplicate}
        onUpdate={onUpdate}
        {...QUESTION_CARD_VARIANTS[SURVEY_TYPES.singleChoice]}
        questionLabel={externalQuestionLabel || `Pregunta ${displayIndex}:`}
        parentOptionLabel={parentOptionLabel}
        parentOptionLabelColor={parentOptionTextColor}
        extraContent={
          <SingleChoiceOptionsSection
            question={question}
            isConditionalParent={isConditionalParent}
            currentLevel={currentLevel}
            displayIndex={displayIndex}
            options={options}
            inputRefs={inputRefs}
            getInputValue={getInputValue}
            handleOptionChange={handleOptionChange}
            handleAddOption={handleAddOption}
            handleKeyDown={handleKeyDown}
            onUpdate={onUpdate}
            onRequestDeleteOption={requestDeleteOption}
            onOpenConditionalMenu={handleOpenConditionalMenu}
            conditionalMenuOptionId={conditionalMenu.optionId}
            showValidation={showValidation}
          />
        }
        canCollapse={!isNested}
        menuItems={menuItems}
        isNested={isNested}
        cardSxOverride={resolvedCardSxOverride}
        headerSxOverride={resolvedHeaderSxOverride}
        questionTextSxOverride={resolvedQuestionTextSxOverride}
        chipSxOverride={externalChipSxOverride}
        extraHeaderContent={
          <>
            {isConditionalParent && (
              <Chip
                label={SINGLE_CHOICE_CARD_TEXTS.hasConditionalsChip}
                size="small"
                sx={{
                  ml: isNested
                    ? SINGLE_CHOICE_CARD_STYLES.conditionalChipNestedMl
                    : SINGLE_CHOICE_CARD_STYLES.conditionalChipDefaultMl,
                  backgroundColor:
                    SINGLE_CHOICE_CARD_STYLES.conditionalChipBackgroundColor,
                  color: SINGLE_CHOICE_CARD_STYLES.conditionalChipTextColor,
                  fontSize: isNested
                    ? SINGLE_CHOICE_CARD_STYLES.conditionalChipNestedFontSize
                    : SINGLE_CHOICE_CARD_STYLES.conditionalChipDefaultFontSize,
                  fontWeight:
                    SINGLE_CHOICE_CARD_STYLES.conditionalChipFontWeight,
                  maxWidth: isNested
                    ? SINGLE_CHOICE_CARD_STYLES.conditionalChipNestedMaxWidth
                    : SINGLE_CHOICE_CARD_STYLES.conditionalChipDefaultMaxWidth,
                  "& .MuiChip-label": {
                    px: isNested
                      ? SINGLE_CHOICE_CARD_STYLES.conditionalChipNestedLabelPx
                      : undefined,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }
                }}
              />
            )}
          </>
        }
      />

      <SingleChoiceConditionalMenu
        anchorEl={conditionalMenu.anchorEl}
        open={Boolean(conditionalMenu.anchorEl)}
        onClose={handleCloseConditionalMenu}
        items={CONDITIONAL_MENU_ITEMS}
        selectedValue={selectedConditionalValue}
        onSelect={handleSelectConditionalType}
      />
    </>
  );
}